import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUser } from '@common-reducers/UserSelectors';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Clock, ClipboardList } from 'lucide-react';
import ContactActionGroup from '@extension/context/content/components/labels-feature/contact-action-group';
import NotesButton from '@extension/context/content/components/labels-feature/notes-button';
import ChatAgentButton from '@extension/context/content/components/message-reminder-button/chat-agent-button';
import ChatReminderButton from '@extension/context/content/components/message-reminder-button/chat-reminder-button';

interface ChatHeaderActionButtonsProps {
    waChatId: string | undefined;
}

export default memo(function ChatHeaderActionButtons({ waChatId }: ChatHeaderActionButtonsProps) {
    const { t } = useTranslation('common');
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const isLabelsEnabled = !user?.isLabelsDisabled;
    const { isOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);

    // openTeamInboxChatThunk (the single chat-open entry point) already set
    // WhatsAppReducer.currentChat to the resolved @c.us id and pre-fetched
    // notes/reminders/bots — the same state WhatsApp Web sets. Read the
    // resolved id from there; fall back to the raw prop during the brief
    // window before the thunk lands.
    const currentChatId = useAppSelector(
        (state) => state.WhatsAppReducer.currentChat?.id?._serialized
    );

    if (!waChatId) return null;

    const resolvedChatId = currentChatId || waChatId;

    const isScheduledOpen = isOpen && componentToShow === 'ChatScheduledMessages';
    const isTasksOpen = isOpen && componentToShow === 'Tasks';

    const handleScheduledClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isScheduledOpen) {
            dispatch(btLayoutSidebarActions.closeSidebar());
        } else {
            dispatch(btLayoutSidebarActions.openWithComponent('ChatScheduledMessages'));
        }
    };

    const handleTasksClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isTasksOpen) {
            dispatch(btLayoutSidebarActions.closeSidebar());
        } else {
            dispatch(btLayoutSidebarActions.openWithComponent('Tasks'));
        }
    };

    return (
        <div className="flex items-center gap-1">
            {/* Labels Feature Buttons (Inbox, Handle Later, Need Attention, Star, Labels) */}
            {isLabelsEnabled && (
                <ContactActionGroup
                    waChatId={resolvedChatId}
                    showStar={true}
                    showLabels={true}
                    showHandleLater={true}
                    showNeedAttention={true}
                    showInbox={true}
                    size={20}
                    gap={4}
                />
            )}

            {/* Divider */}
            {isLabelsEnabled && (
                <div className="w-px h-5 mx-2" style={{ backgroundColor: 'var(--wa-divider)' }} />
            )}

            {/* Scheduled Messages Button */}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            className="p-1.5 rounded transition-colors cursor-pointer hover:bg-black/5"
                            style={{ color: isScheduledOpen ? 'var(--wa-accent)' : 'var(--wa-secondary-text)' }}
                            onClick={handleScheduledClick}
                        >
                            <Clock size={20} strokeWidth={1.8} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{t('teamInbox.scheduledMessages')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            {/* Tasks Button */}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            className="p-1.5 rounded transition-colors cursor-pointer hover:bg-black/5"
                            style={{ color: isTasksOpen ? 'var(--wa-accent)' : 'var(--wa-secondary-text)' }}
                            onClick={handleTasksClick}
                        >
                            <ClipboardList size={20} strokeWidth={1.8} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{t('teamInbox.tasks')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            {/* Notes Button — extension component, auto-fetches notes for chatId
                and renders an unread badge so the count matches WhatsApp web.
                Pass the resolved c.us id (notes are not stored against @lid). */}
            <NotesButton waChatId={resolvedChatId} size={20} />

            {/* Bot icon (QuickAgents) — same component the extension renders in
                tasks-action-bar so the team-inbox header mirrors WhatsApp web. */}
            <ChatAgentButton size={20} />

            {/* Reminders Button — extension component */}
            <ChatReminderButton size={20} />
        </div>
    );
});
